/**
 * A simple per-instance event emitter, mirroring the subset of jQuery's
 * `.on()`/`.trigger()` semantics this codebase relies on: handlers receive
 * an `{ type }` event object first, followed by the triggered data spread
 * as separate arguments when it's an array, or as a single argument
 * otherwise.
 */
export default class LPEvent {
    /**
     * Create a new LPEvent instance.
     *
     * @param {string} key - The event key, exposed to handlers as `event.type`.
     */
    constructor(key) {
        this._key = key;
        /** @type {((...args: any[]) => void)[]} */
        this._handlers = [];
    }

    /**
     * Bind a handler to this event.
     *
     * @param {(...args: any[]) => void} handler - The callback to invoke when the event is triggered.
     * @returns {void}
     */
    bind(handler) {
        this._handlers.push(handler);
    }

    /**
     * Trigger this event, optionally passing data to handlers.
     *
     * @param {any} data - Data to pass to the event handlers.
     * @returns {void}
     */
    trigger(data) {
        const event = { type: this._key };
        // Mirrors jQuery's `data == null` check: `null` and `undefined` both
        // mean "no extra args".
        const extraArgs =
            data == null ? [] : Array.isArray(data) ? data : [data];

        for (const handler of this._handlers) {
            handler(event, ...extraArgs);
        }
    }
}
